// ABOUTME: Onboarding wizard page for first-time users after signup.
// ABOUTME: Guides users through name collection, data source connection, and first investigation.

import { useState, useCallback, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Database, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DataSourceWizard } from '@/components/data-source/DataSourceWizard';
import { CsvUploadWizard } from '@/components/data-source/CsvUploadWizard';
import { SampleDatasetPicker } from '@/components/data-source/SampleDatasetPicker';
import { DataSourceSelector, type DataSourceOption } from '@/components/data-source/DataSourceSelector';
import { ComingSoonScreen } from '@/components/data-source/ComingSoonScreen';
import { dataSourceApi } from '@/services/api/dataSourceClient';
import { ApiError, userApi, workspaceMetricsApi } from '@/services/api/client';
import { useApp } from '@/context/AppContext';
import type { DataSource, DemoDatasetId } from '@/types/api';

type OnboardingStep = 'profile' | 'data-source' | 'data-source-sample' | 'data-source-selector' | 'data-source-connect' | 'data-source-csv' | 'data-source-coming-soon' | 'complete';

// Map frontend step names to backend step names for tracking
const stepToBackendStep: Record<OnboardingStep, 'not_started' | 'profile' | 'welcome' | 'data_source' | 'completed'> = {
  'profile': 'profile',
  'data-source': 'data_source',
  'data-source-sample': 'data_source',
  'data-source-selector': 'data_source',
  'data-source-connect': 'data_source',
  'data-source-csv': 'data_source',
  'data-source-coming-soon': 'data_source',
  'complete': 'completed',
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Check if we should skip to data-source step (e.g., coming from HomePage)
  const skipToDataSource = searchParams.get('step') === 'data-source';
  // When mode=add, user is adding an additional data source (already has one)
  const isAddMode = searchParams.get('mode') === 'add';

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('profile');
  const [firstName, setFirstName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingOnboardingStatus, setIsCheckingOnboardingStatus] = useState(true);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [selectedComingSoonSource, setSelectedComingSoonSource] = useState<DataSourceOption | null>(null);
  const { selfServiceDataConfigEnabled } = useApp();

  // Track onboarding progress in the database for churn analysis
  const trackOnboardingProgress = useCallback(async (step: OnboardingStep) => {
    const backendStep = stepToBackendStep[step];
    try {
      await userApi.updateOnboardingProgress({ step: backendStep });
    } catch (err) {
      // Don't block the user flow if tracking fails
      console.error('Failed to track onboarding progress:', err);
    }
  }, []);

  // Check onboarding status and determine where to resume
  // Uses the backend's onboarding_step to pick up where user left off
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        // Check if user has data sources (completed onboarding)
        const metrics = await workspaceMetricsApi.get();
        if (metrics.data_sources > 0 && !isAddMode) {
          // User already has data sources, redirect to home
          navigate('/', { replace: true });
          return;
        }

        // Handle explicit skip to data-source step
        if (skipToDataSource) {
          // In add mode, skip the split-path chooser and go straight to selector
          setCurrentStep(isAddMode ? 'data-source-selector' : 'data-source');
          trackOnboardingProgress('data-source');
          setIsCheckingOnboardingStatus(false);
          return;
        }

        // Fetch user profile to get onboarding_step
        const profile = await userApi.me();
        const backendStep = profile.preferences.onboarding_step;

        // Map backend step to frontend step and determine where to resume
        let resumeStep: OnboardingStep = 'profile';
        if (backendStep === 'welcome' || backendStep === 'data_source' || backendStep === 'completed') {
          // User has already completed profile, go to split flow
          resumeStep = 'data-source';
          // Also capture their saved name for display
          if (profile.name) {
            // Name already saved in profile
          }
        }

        setCurrentStep(resumeStep);
        // Only track if we're starting fresh (not_started or profile)
        if (backendStep === 'not_started') {
          trackOnboardingProgress(resumeStep);
        }
      } catch (err) {
        // On error, start from beginning
        console.debug('Failed to check onboarding status:', err);
        trackOnboardingProgress('profile');
      } finally {
        setIsCheckingOnboardingStatus(false);
      }
    };

    checkOnboardingStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, skipToDataSource, isAddMode]);

  const handleSaveName = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!firstName.trim()) {
      setNameError('Please enter your name');
      return;
    }

    setIsSavingName(true);
    setNameError(null);

    try {
      await userApi.updateProfile({ name: firstName.trim() });
      setCurrentStep('data-source');
      // Track progress to data source step
      trackOnboardingProgress('data-source');
    } catch (err) {
      console.error('Failed to save name:', err);
      setNameError('Failed to save your name. Please try again.');
    } finally {
      setIsSavingName(false);
    }
  }, [firstName, trackOnboardingProgress]);

  const handleDataSourceComplete = useCallback((_dataSource: DataSource) => {
    trackOnboardingProgress('complete');
    navigate(isAddMode ? '/data-sources' : '/');
  }, [navigate, trackOnboardingProgress, isAddMode]);

  const handleDataSourceCancel = useCallback(() => {
    setCurrentStep('data-source-selector');
  }, []);

  const handleExploreSampleData = useCallback(() => {
    setCurrentStep('data-source-sample');
    trackOnboardingProgress('data-source');
  }, [trackOnboardingProgress]);

  const handleConnectRealData = useCallback(() => {
    setCurrentStep('data-source-selector');
    trackOnboardingProgress('data-source');
  }, [trackOnboardingProgress]);

  const handleSelectorDatabricks = useCallback(() => {
    setCurrentStep('data-source-connect');
  }, []);

  const handleSelectorCsv = useCallback(() => {
    setCurrentStep('data-source-csv');
  }, []);

  const handleSelectorComingSoon = useCallback((source: DataSourceOption) => {
    setSelectedComingSoonSource(source);
    setCurrentStep('data-source-coming-soon');
  }, []);

  const handleComingSoonBack = useCallback(() => {
    setSelectedComingSoonSource(null);
    setCurrentStep('data-source-selector');
  }, []);

  const handleSampleDatasetSelect = useCallback(async (datasetId: DemoDatasetId) => {
    setIsDemoLoading(true);
    setDemoError(null);
    try {
      await dataSourceApi.createDemo({ dataset_id: datasetId });
      trackOnboardingProgress('complete');
      navigate(isAddMode ? '/data-sources' : '/');
    } catch (err: unknown) {
      console.error('Failed to create demo data source:', err);
      let message: string;

      if (err instanceof ApiError) {
        switch (err.status) {
          case 503:
            message = 'Your workspace is still being set up. This usually takes about a minute — please wait and try again.';
            break;
          case 409:
            // Already has a demo data source — just navigate away
            trackOnboardingProgress('complete');
            navigate(isAddMode ? '/data-sources' : '/');
            return;
          default:
            message = err.message || 'Something went wrong setting up sample data. Please try again.';
        }
      } else if (err instanceof TypeError && (err.message.includes('fetch') || err.message.includes('network'))) {
        message = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        message = 'Something went wrong setting up sample data. Please try again.';
      }

      setDemoError(message);
    } finally {
      setIsDemoLoading(false);
    }
  }, [navigate, trackOnboardingProgress, isAddMode]);

  const handleSampleDataCancel = useCallback(() => {
    setCurrentStep('data-source');
    setDemoError(null);
  }, []);

  // Show loading state while checking if user should be on this page
  if (isCheckingOnboardingStatus) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-surface-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Profile step - collect user's name
  if (currentStep === 'profile') {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-md mx-auto px-6 py-16">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/oleum-logo.png"
              alt="Oleum BI"
              className="w-20 h-20 object-contain"
            />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-surface-900 mb-3">
              Welcome to Oleum BI
            </h1>
            <p className="text-surface-600">
              What should we call you?
            </p>
          </div>

          {/* Name Form */}
          <form onSubmit={handleSaveName} className="space-y-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-surface-700 mb-2">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                autoFocus
                className="w-full px-4 py-3 rounded-sm border border-surface-300 bg-surface-0 text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {nameError && (
                <p className="mt-2 text-sm text-error">{nameError}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSavingName}
            >
              {isSavingName ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Sample dataset picker sub-step
  if (currentStep === 'data-source-sample') {
    return (
      <div className="h-full overflow-y-auto p-6">
        <SampleDatasetPicker
          onSelect={handleSampleDatasetSelect}
          onCancel={handleSampleDataCancel}
          isLoading={isDemoLoading}
          error={demoError}
        />
      </div>
    );
  }

  // Data source type selector grid
  if (currentStep === 'data-source-selector') {
    return (
      <div className="h-full overflow-y-auto p-6">
        <DataSourceSelector
          onSelectDatabricks={handleSelectorDatabricks}
          onSelectCsv={handleSelectorCsv}
          csvUploadEnabled={selfServiceDataConfigEnabled}
          onSelectComingSoon={handleSelectorComingSoon}
          onBack={() => isAddMode ? navigate('/data-sources') : setCurrentStep('data-source')}
        />
      </div>
    );
  }

  // CSV upload wizard
  if (currentStep === 'data-source-csv') {
    return (
      <div className="h-full overflow-y-auto p-6">
        <CsvUploadWizard
          onComplete={() => {
            trackOnboardingProgress('complete');
            navigate(isAddMode ? '/data-sources' : '/');
          }}
          onCancel={() => setCurrentStep('data-source-selector')}
        />
      </div>
    );
  }

  // Coming soon interstitial for unsupported data sources
  if (currentStep === 'data-source-coming-soon' && selectedComingSoonSource) {
    return (
      <div className="h-full overflow-y-auto flex items-center justify-center p-6">
        <ComingSoonScreen
          sourceName={selectedComingSoonSource.name}
          sourceType={selectedComingSoonSource.sourceType}
          logoSrc={selectedComingSoonSource.logoSrc}
          onBack={handleComingSoonBack}
        />
      </div>
    );
  }

  // Real data source connection sub-step
  if (currentStep === 'data-source-connect') {
    return (
      <div className="h-full overflow-y-auto flex items-center justify-center p-6">
        <DataSourceWizard
          onComplete={handleDataSourceComplete}
          onCancel={handleDataSourceCancel}
        />
      </div>
    );
  }

  // Split path chooser (data-source step)
  if (currentStep === 'data-source') {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-surface-900 mb-3">
              How would you like to get started?
            </h2>
            <p className="text-surface-600">
              Try with sample data or connect your own data.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Explore with sample data */}
            <button
              onClick={handleExploreSampleData}
              className="text-left p-6 rounded-sm border-2 border-surface-200 bg-surface-0 hover:border-primary-300 hover:bg-primary-50/50 transition-all group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                <Database className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-surface-900 mb-2">
                Explore with sample data
              </h3>
              <p className="text-sm text-surface-600">
                Try Obsidian BI with curated datasets. No setup required -- start asking questions immediately.
              </p>
            </button>

            {/* Connect your Databricks */}
            <button
              onClick={handleConnectRealData}
              className="text-left p-6 rounded-sm border-2 border-surface-200 bg-surface-0 hover:border-primary-300 hover:bg-primary-50/50 transition-all group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-full bg-surface-100 flex items-center justify-center mb-4 group-hover:bg-surface-200 transition-colors">
                <Database className="w-6 h-6 text-surface-600" />
              </div>
              <h3 className="text-lg font-semibold text-surface-900 mb-2">
                Connect your data
              </h3>
              <p className="text-sm text-surface-600">
                Connect your own workspace and start analyzing your data right away.
              </p>
            </button>
          </div>

        </div>
      </div>
    );
  }

  // Fallback: redirect to split flow
  return null;
}
